import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/Header";
import { z } from "zod";

/** Only same-origin relative paths may be used as a post-sign-in return target. */
const safeNextPath = (raw: string | null): string | null => {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};
import { 
  Eye, EyeOff, Shield, CheckCircle2, XCircle, 
  User, Music, Building2, Users, Sparkles, Mail, Loader2
} from "lucide-react";

// Security validation schemas
const emailSchema = z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters");

const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .max(100, "Password must be less than 100 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

const usernameSchema = z.string()
  .trim()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be less than 30 characters")
  .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores");

const fullNameSchema = z.string().trim().max(100, "Name must be less than 100 characters");

// Route users to the correct dashboard based on their user_type
const getDashboardRoute = (userType: string | null | undefined): string => {
  switch (userType) {
    case "creator":
      return "/dashboard/creator/feeds";
    case "venue":
      return "/dashboard/venue/groove-insights";
    case "groover":
    default:
      return "/dashboard/groover/feeds";
  }
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [userType, setUserType] = useState<"groover" | "creator" | "venue">("groover");
  const [authTab, setAuthTab] = useState<"signin" | "signup">("signin");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string[];
  }>({ score: 0, feedback: [] });

  // Forgot password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotCooldown, setForgotCooldown] = useState(0);

  useEffect(() => {
    if (forgotCooldown <= 0) return;
    const timer = setInterval(() => {
      setForgotCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [forgotCooldown]);

  const openForgotPassword = () => {
    setForgotEmail(email || "");
    setForgotSent(false);
    setForgotOpen(true);
  };

  const handleSendResetLink = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const emailResult = emailSchema.safeParse(forgotEmail);
    if (!emailResult.success) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: emailResult.error.issues[0].message,
      });
      return;
    }

    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(emailResult.data, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setForgotLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not send reset link",
        description: error.message,
      });
      return;
    }

    setForgotSent(true);
    setForgotCooldown(60);
    toast({
      title: "Reset link sent",
      description: `Check ${emailResult.data} for instructions to reset your password.`,
    });
  };

  const tierPricing = "Free to join";

  // Password strength checker
  const checkPasswordStrength = (pass: string) => {
    const feedback: string[] = [];
    let score = 0;

    if (pass.length >= 8) score++;
    if (pass.length >= 12) score++;
    if (/[A-Z]/.test(pass)) { score++; } else { feedback.push("Add uppercase letter"); }
    if (/[a-z]/.test(pass)) { score++; } else { feedback.push("Add lowercase letter"); }
    if (/[0-9]/.test(pass)) { score++; } else { feedback.push("Add number"); }
    if (/[^A-Za-z0-9]/.test(pass)) { score++; feedback.push("Strong!"); }

    setPasswordStrength({ score, feedback });
  };

  const handleSignUp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    const cleanUsername = username.startsWith('@') ? username.slice(1).trim() : username.trim();
    const cleanFullName = fullName.trim();
    const cleanPassword = password.trim();

    try {
      emailSchema.parse(cleanEmail);
      usernameSchema.parse(cleanUsername);
      fullNameSchema.parse(cleanFullName);
      passwordSchema.parse(cleanPassword);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: error.errors[0].message,
        });
        setLoading(false);
        return;
      }
    }

    const { error } = await supabase.auth.signUp({
      email: cleanEmail,
      password: cleanPassword,
      options: {
        emailRedirectTo: `${window.location.origin}${safeNextPath(searchParams.get("next")) ?? "/"}`,
        data: {
          username: cleanUsername,
          full_name: cleanFullName,
          // The account keeps the role picked at sign up so the matching
          // dashboard/bottom nav loads. Pro tools stay locked behind the
          // in-app upgrade popup until the add-on is paid for.
          user_type: userType,
          intended_role: userType,
        },
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message,
      });
      setLoading(false);
      return;
    }

    // The welcome email is sent from the signed-in session (the server only
    // ever mails the caller's own address), so queue it for first sign-in.
    try {
      localStorage.setItem(
        "mcg_pending_welcome",
        JSON.stringify({ name: cleanFullName || cleanUsername, role: userType }),
      );
    } catch { /* storage unavailable — the email is simply skipped */ }

    toast({
      title: "Sign up successful!",
      description: "Please check your email to confirm your account, then sign in.",
      duration: 5000,
    });
    // Always send new accounts (including venues) back to the sign-in tab.
    setAuthTab("signin");
    setEmail(cleanEmail);
    setPassword("");
    setUsername("");
    setFullName("");
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();

    try {
      emailSchema.parse(cleanEmail);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: error.errors[0].message,
        });
        setLoading(false);
        return;
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error.message,
      });
      setLoading(false);
      return;
    }
    
    if (data.user) {
      // When sign-in was triggered by an app-connection request, return there.
      const nextPath = safeNextPath(searchParams.get("next"));
      if (nextPath) {
        window.location.href = nextPath;
        return;
      }

      // Send the queued welcome email now that we have a real session.
      try {
        const pending = localStorage.getItem("mcg_pending_welcome");
        if (pending) {
          localStorage.removeItem("mcg_pending_welcome");
          const parsed = JSON.parse(pending) as { name?: string; role?: string };
          void supabase.functions
            .invoke("send-welcome-email", {
              body: {
                name: parsed.name ?? "",
                role: parsed.role ?? "",
                dashboardUrl: `${window.location.origin}/dashboard`,
              },
            })
            .catch(() => {});
        }
      } catch { /* ignore */ }

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_type, verification_status")
        .eq("id", data.user.id)
        .single();

      const profileUserType = profile?.user_type;

      // Agency / platform staff go straight to the portal their roles protect.
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id);
      const roleNames = (roles ?? []).map((r) => r.role as string);
      const staffPath =
        roleNames.includes("admin") || roleNames.includes("platform_control")
          ? "/admin/platform-control"
          : roleNames.includes("creator_management")
            ? "/admin/creator-management"
            : roleNames.includes("venue_oversight")
              ? "/admin/venue-oversight"
              : null;
      if (staffPath) {
        toast({ title: "Welcome back!", description: "Opening your agency portal." });
        navigate(staffPath);
        setLoading(false);
        return;
      }

      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });

      // Everyone reaches a dashboard. Free accounts land on the Groover
      // dashboard where the upgrade button unlocks Creator/Venue tools.
      if (profileUserType === "groover" || !profileUserType) {
        if (profile?.verification_status === "pending") {
          await supabase
            .from("profiles")
            .update({ verification_status: "verified", verified_at: new Date().toISOString() })
            .eq("id", data.user.id);
        }
        navigate("/dashboard/groover/feeds");
      } else {
        navigate(getDashboardRoute(profileUserType));
      }
    }
    setLoading(false);
  };



  const userTypeConfig = {
    groover: {
      icon: Users,
      label: "Groover",
      description: "Music enthusiast & event-goer",
      price: "Free to join",
      features: ["Browse events", "Save favorites", "Connect with creators"],
      bgColor: "bg-black hover:bg-gray-900 border border-gray-800",
      selectedColor: "bg-black border-white"
    },
    creator: {
      icon: Music,
      label: "Creator",
      description: "Artists, DJs & Performers",
      price: "Free to join",
      features: ["Full Groover access", "Upgrade in-app to get verified", "Then: ticketing, bookings & payouts"],
      bgColor: "bg-black hover:bg-gray-900 border border-gray-800",
      selectedColor: "bg-black border-white"
    },
    venue: {
      icon: Building2,
      label: "Venue",
      description: "Clubs, Bars & Event Spaces",
      price: "Free to join", 
      features: ["Full Groover access", "Upgrade in-app to get verified", "Then: events, ticketing & analytics"],
      bgColor: "bg-black hover:bg-gray-900 border border-gray-800",
      selectedColor: "bg-black border-white"
    },
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-black text-white relative overflow-hidden pt-20 pb-10">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,hsla(0,0%,100%,0.02)_1px,transparent_1px),linear-gradient(to_bottom,hsla(0,0%,100%,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
        </div>

        {/* Auth Form Container */}
        <div className="relative z-10 w-full max-w-2xl mx-auto px-4 pt-8">
          <div className="w-full p-4 sm:p-8 bg-black/80 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-2xl">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-900 to-black border border-gray-800 mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Secure Authentication
                </h2>
                <p className="text-gray-400">Enterprise-grade security for your music journey</p>
              </div>

              <Tabs value={authTab} onValueChange={(v) => setAuthTab(v as "signin" | "signup")} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-black border border-gray-800 p-1 rounded-xl">
                  <TabsTrigger 
                    value="signin" 
                    className="data-[state=active]:bg-white data-[state=active]:text-black rounded-lg transition-all"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="signup" 
                    className="data-[state=active]:bg-white data-[state=active]:text-black rounded-lg transition-all"
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email" className="text-gray-300">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="signin-password" className="text-gray-300">Password</Label>
                        <button
                          type="button"
                          onClick={openForgotPassword}
                          disabled={loading}
                          className="text-xs text-gray-400 hover:text-white underline underline-offset-4 transition-colors disabled:opacity-50"
                        >
                          Forgot password?
                        </button>
                      </div>

                      <div className="relative">
                        <Input
                          id="signin-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          autoComplete="current-password"
                          className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-6 transition-all"
                      disabled={loading}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                    
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <div className="space-y-6">
                    {/* User Type Selection */}
                    <div className="space-y-4">
                      <Label className="text-gray-300 text-base">Select your account type</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        {(Object.keys(userTypeConfig) as Array<keyof typeof userTypeConfig>).map((type) => {
                          const config = userTypeConfig[type];
                          const Icon = config.icon;
                          const isSelected = userType === type;
                          
                          return (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setUserType(type)}
                              className={`relative p-5 rounded-xl border-2 transition-all duration-300 ${
                                isSelected 
                                  ? config.selectedColor + ' scale-[1.02] shadow-lg'
                                  : config.bgColor
                              }`}
                            >
                              <div className="flex flex-col items-center gap-3">
                                <Icon className={`w-7 h-7 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                                <span className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                  {config.label}
                                </span>
                                <span className={`text-sm font-bold ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                                  {config.price}
                                </span>
                                <div className="text-xs text-gray-500 text-center mt-2">
                                  {config.description}
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-white" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Signing up is completely free — no payment required. Creator and Venue tools are optional
                        add-ons you can activate later from your profile inside the app.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="signup-username" className="text-gray-300">Username</Label>
                        <Input
                          id="signup-username"
                          type="text"
                          placeholder="@username"
                          value={username}
                          onChange={(e) => {
                            const value = e.target.value;
                            setUsername(value.startsWith('@') ? value : '@' + value);
                          }}
                          required
                          autoComplete="username"
                          maxLength={31}
                          className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="signup-name" className="text-gray-300">Full Name</Label>
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          autoComplete="name"
                          maxLength={100}
                          className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-email" className="text-gray-300">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        maxLength={255}
                        className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-password" className="text-gray-300">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            checkPasswordStrength(e.target.value);
                          }}
                          required
                          minLength={8}
                          maxLength={100}
                          autoComplete="new-password"
                          className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {password && (
                        <div className="space-y-3 mt-4 p-4 rounded-xl bg-black border border-gray-800">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-300">Password Strength</span>
                            <span className={`text-sm font-semibold ${
                              passwordStrength.score <= 2 ? "text-red-400" :
                              passwordStrength.score <= 4 ? "text-yellow-400" : "text-green-400"
                            }`}>
                              {passwordStrength.score <= 2 ? "Weak" : passwordStrength.score <= 4 ? "Medium" : "Strong"}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            {[...Array(6)].map((_, i) => (
                              <div
                                key={i}
                                className={`h-2 flex-1 rounded-full transition-all ${
                                  i < passwordStrength.score
                                    ? passwordStrength.score <= 2
                                      ? "bg-red-500"
                                      : passwordStrength.score <= 4
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                    : "bg-gray-800"
                                }`}
                              />
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {passwordStrength.feedback.map((item, i) => (
                              <span key={i} className="text-xs text-gray-500 flex items-center gap-2">
                                {item === "Strong!" ? (
                                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                                ) : (
                                  <XCircle className="w-3 h-3 text-gray-700" />
                                )}
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button 
                      type="button" 
                      className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-6 mt-6 transition-all"
                      onClick={() => handleSignUp()}
                      disabled={loading}
                    >
                      <span className="flex items-center justify-center gap-2">
                        {loading ? "Signing up..." : `Join as ${userTypeConfig[userType].label}`}
                        <Sparkles className="w-5 h-5" />
                      </span>
                    </Button>

                  </div>
                </TabsContent>
              </Tabs>
            </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
          <DialogHeader className="space-y-2">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-purple-400 mb-1">
              <Mail className="w-5 h-5" />
            </div>
            <DialogTitle className="text-xl font-bold">Reset Your Password</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="space-y-4 py-3 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-950/60 border border-emerald-800/60 flex items-center justify-center mx-auto text-emerald-400">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-white">Check your email</h4>
                <p className="text-xs text-gray-400">
                  We've sent a password reset link to <span className="text-white font-medium">{forgotEmail}</span>.
                </p>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={forgotCooldown > 0 || forgotLoading}
                  onClick={handleSendResetLink}
                  className="border-zinc-800 hover:bg-zinc-900 text-gray-300"
                >
                  {forgotCooldown > 0
                    ? `Resend link in ${forgotCooldown}s`
                    : forgotLoading
                    ? "Sending..."
                    : "Resend reset link"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  className="bg-white hover:bg-gray-200 text-black font-semibold text-xs"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSendResetLink} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-gray-300 text-sm">
                  Email Address
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-purple-500 focus:ring-purple-500/20"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setForgotOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="bg-white hover:bg-gray-200 text-black font-semibold"
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Auth;
